/**
 * useStores — ViewModel for /manage/stores. List + add + edit + delete.
 * The default store (is_default = 1) is editable only via API rejection
 * at the repo level — the UI hides Edit/Delete on that row entirely.
 *
 * No "set default" action — the seeded row owns that flag.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { StoreDto, StoreStatus } from '../models/store.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'

export type { StoreDto, StoreStatus } from '../models/store.model'

export interface CreateStoreInput {
  readonly slug: string
  readonly display_name: string
  readonly status?: StoreStatus
}

export interface UpdateStoreInput {
  readonly slug?: string
  readonly display_name?: string
  readonly status?: StoreStatus
}

export interface UseStoresReturn {
  readonly items: StoreDto[]
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
  readonly add: (input: CreateStoreInput) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (
    id: string,
    patch: UpdateStoreInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: { readonly items: StoreDto[] }
}

export function useStores(): UseStoresReturn {
  const { authedFetch } = useAuth()
  const [items, setItems] = useState<StoreDto[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState<number>(0)

  useEffect(() => {
    let cancelled = false
    const run = async (): Promise<void> => {
      setIsLoading(true)
      setError(null)
      const res = await authedFetch(APIROUTES.MANAGESTORES)
      if (cancelled) return
      if (!res.ok) {
        const { message } = await parseApiError(res)
        if (!cancelled) {
          setError(message)
          setIsLoading(false)
        }
        return
      }
      const body = (await res.json()) as ListBody
      if (cancelled) return
      setItems(body.data.items)
      setIsLoading(false)
    }
    void run()
    return () => { cancelled = true }
  }, [authedFetch, tick])

  const reload = useCallback((): void => setTick((t) => t + 1), [])

  const add = useCallback(
    async (input: CreateStoreInput) => {
      const r = await mutateJson<StoreDto>(authedFetch, APIROUTES.MANAGESTORES, 'POST', input)
      if (!r.ok) return r
      reload()
      return { ok: true as const }
    },
    [authedFetch, reload]
  )

  const update = useCallback(
    async (id: string, patch: UpdateStoreInput) => {
      const r = await mutateJson<StoreDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGESTOREITEM, { id }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      reload()
      return { ok: true as const }
    },
    [authedFetch, reload]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGESTOREITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      reload()
      return { ok: true as const }
    },
    [authedFetch, reload]
  )

  return { items, isLoading, error, reload, add, update, remove }
}
